'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';

/**
 * Da cosa dipende la soglia di incentivabilità del mese (`GET
 * /api/incentives/soglia`): se almeno un impianto ha il contributo in conto
 * capitale e quale quota dell'immessa viene da quegli impianti (INB-313,
 * INB-324). `undefined` finché non arriva: la pagina ricade sul valore di CER
 * solo per quel breve istante.
 *
 * Dipende dal contributo di ogni impianto e dal valore di CER: quando uno dei
 * due cambia, da un altro pannello o da un'altra scheda, la query si invalida.
 */
export function useSogliaPreview(
	orgId: string | null | undefined,
	mese: number,
	anno: number,
): { hasPnrrGrant: boolean; quotaImmessaPnrr: number | null } | undefined {
	const queryClient = useQueryClient();
	const { data } = useQuery({
		queryKey: ['incentives-soglia', orgId, mese, anno],
		enabled: !!orgId,
		queryFn: () =>
			api.get<{ hasPnrrGrant: boolean; quotaImmessaPnrr?: number | null }>(
				`/api/incentives/soglia?orgId=${orgId}&mese=${mese}&anno=${anno}`,
			),
	});

	useEffect(() => {
		if (!orgId) return;
		const invalidate = () => {
			void queryClient.invalidateQueries({ queryKey: ['incentives-soglia', orgId] });
		};
		const unsubscribes = [
			subscribeToTable(
				`soglia-production-plants-${orgId}`,
				{ event: '*', schema: 'public', table: 'production_plants' },
				invalidate,
			),
			subscribeToTable(
				`soglia-organizations-${orgId}`,
				{ event: 'UPDATE', schema: 'public', table: 'organizations', filter: `id=eq.${orgId}` },
				invalidate,
			),
		];
		return () => {
			for (const unsubscribe of unsubscribes) unsubscribe();
		};
	}, [orgId, queryClient]);

	return data
		? { hasPnrrGrant: data.hasPnrrGrant, quotaImmessaPnrr: data.quotaImmessaPnrr ?? null }
		: undefined;
}
